<template>
  <fragment>
    <fragment v-if="fetchingData">
      <b-row>
        <b-col>
          <Loader middle />
        </b-col>
      </b-row>
    </fragment>

    <fragment v-else>
      <b-row class="mb-5 mt-3">
        <b-col>
          <h2 class="text-success" v-if="reviewable">You are reviewing: {{ reviewForm.target.name }}</h2>
          <h2 class="text-success" v-if="!reviewable">
            Review form: {{ reviewForm.target.name }}
            <StatusView :status="reviewForm.status" />
          </h2>
          <h4>
            <i>
              The importance of feedback in the workplace is hard to overestimate: sharing information on what can and
              needs to be improved helps optimize work process and get things done in less time. Feedback is of great
              help in leadership and communication: it creates a clear picture and increases transparency.
            </i>
          </h4>

          <p>
            <span class="small">There are {{ listReviewPoint.length }} item(s) in the review form:</span>
          </p>
        </b-col>
      </b-row>
      <b-row v-for="(criteria, idx) in listReviewPoint" :key="criteria.id">
        <b-col>
          <div class="card mb-5">
            <div class="card-header">
              <h3 class="text-secondary">{{ idx + 1 }}. {{ criteria.title }}</h3>
            </div>
            <div class="card-body">
              <b-row>
                <b-col md="6">
                  {{ criteria.explanation }}
                </b-col>
                <b-col md="6">
                  <CreateReviewPointForm
                    :review-point="criteria.reviewPoint"
                    :disabled="!reviewable"
                    @onSubmit="f => setPoint({ ...criteria.reviewPoint, ...f })"
                  />
                </b-col>
              </b-row>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row align-h="center" class="mb-5" v-if="reviewable">
        <b-col md="3" sm="4">
          <b-button block variant="outline-success" @click="finishReview" size="lg">
            finish review <strong>{{ reviewForm.target.name }}</strong>
          </b-button>
        </b-col>
      </b-row>
    </fragment>
  </fragment>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CreateReviewPointForm from '../components/CreateReviewPointForm';
import utils from '../utils';
import Vue from 'vue';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import StatusView from '../components/StatusView';
import Loader from '@/components/Loader';

Vue.use(VueSweetalert2);

export default {
  name: 'PerformingReviewForm',
  components: { StatusView, CreateReviewPointForm, Loader },
  data() {
    return {
      REVIEW_STATUS: utils.REVIEW_STATUS,
      points: [5, 4, 3, 2, 1],
      fetchingData: true,
    };
  },
  computed: {
    ...mapGetters('review', ['listReviewForm', 'reviewForm', 'reviewable']),
    reviewFormId() {
      return this.$route.params.id;
    },
    listReviewPoint() {
      if (!this.reviewForm?.groupCriteria) return [];

      let rs = this.reviewForm.groupCriteria.criteria;
      const oldReviews = this.reviewForm.reviews;
      const oldReviewMap = oldReviews.reduce((a, c) => {
        const id = utils.composeIds(c.reviewFormId, c.criteriaId);
        return a.set(id, c);
      }, new Map());

      rs = rs.map(({ id: criteriaId, title, explanation }) => {
        const reviewFormId = utils.fromGlobalId(this.reviewForm.id).id;
        const id = utils.composeIds(reviewFormId, utils.fromGlobalId(criteriaId).id);
        const oldReview = oldReviewMap.get(id) || {};
        return {
          title,
          explanation,
          reviewPoint: {
            reviewFormId: this.reviewForm.id,
            criteriaId,
            point: oldReview.point,
            comment: oldReview.comment,
          },
        };
      });
      return rs;
    },
  },
  methods: {
    ...mapActions('review', ['fetchReviewForm', 'createReviewPoint', 'setReviewFormStatus']),
    setPoint(reviewPoint) {
      this.createReviewPoint(reviewPoint);
    },
    finishReview() {
      this.$swal
        .fire({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: "Yes, I'm done!",
        })
        .then(result => {
          if (result.value) {
            this.setReviewFormStatus({ reviewFormId: this.reviewFormId, status: this.REVIEW_STATUS.FINISH }).then(
              () => {
                this.$swal.fire('Done!', 'Your reviews are valuable.', 'success');
              }
            );
          }
        });
    },
  },
  mounted() {
    this.fetchReviewForm(this.reviewFormId).then(() => (this.fetchingData = false));
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/colors';
h3,
h4 {
  color: $darkBlue !important;
}

p {
  margin-top: 1.5rem;
  font-size: 1.8rem;
  font-weight: bold;
}
</style>
